import { LocationStrategy } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { environment } from 'src/environments/environment';
import {
  ApproverCheckLogin,
  ApproverLogout,
} from './auth/store/approver/approver-auth.actions';
import { CheckLogin, Logout } from './auth/store/auth.actions';
import {
  SearcherCheckLogin,
  SearcherLogout,
} from './auth/store/searcher/searcher-auth.actions';
import { IStore } from './core/root.store';
import { RemoveAllEmergencyContacts } from './core/store/emergency-contact/emergency-contact.actions';
import { RemoveAllLots } from './core/store/lot/lot.actions';
import { RemoveAllPets } from './core/store/pet/pet.actions';
import { RemoveAllProfiles } from './core/store/resident-profile/resident-profile.actions';
import { ApproverSetSubDomain } from './core/store/ui/approver/ui.actions';
import {
  FetchLotsAndUnits,
  FetchLotsAndUnitsWithStrataPlan,
  SetStrataPlanFriendlyName,
  SetSubDomain,
} from './core/store/ui/ui.actions';
import {
  getIsLoggedIn,
  getIsLoginCheckComplete,
  getStrataPlanNumberFriendlyName,
} from './core/store/ui/ui.selectors';
import { RemoveAllVehicles } from './core/store/vehicle/vehicle.actions';
import { UserLogin } from './shared/services/roles.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'StratSphere Members';
  private componentDestroyed$ = new Subject<void>();
  user_login: string;
  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private store$: Store<IStore>,
    private locationStrategy: LocationStrategy,
    private googleTagManagerService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    let old_friendlyName = localStorage.getItem(
      environment.ui.strata_plan_friendly_name
    );
    let new_friendlyname = this.getStrataPlanNumberFriendlyName();
    if (new_friendlyname !== null && old_friendlyName !== new_friendlyname) {
      // searcher logout
      localStorage.removeItem(environment.auth.user_login);
      this.store$.dispatch(new SearcherLogout());
      // approver Logout
      localStorage.removeItem(environment.auth.user_login);
      this.store$.dispatch(new ApproverLogout());
      // admin Logout
      this.store$.dispatch(new Logout());
      this.store$.dispatch(new RemoveAllProfiles());
      this.store$.dispatch(new RemoveAllLots());
      this.store$.dispatch(new RemoveAllEmergencyContacts());
      this.store$.dispatch(new RemoveAllPets());
      this.store$.dispatch(new RemoveAllVehicles());
    }
    this.setUiStore(true);
    this.checkPathUserLogin();
    this.user_login = localStorage.getItem(environment.auth.user_login);

    this.store$.dispatch(new CheckLogin());
    this.store$
      .select(getIsLoginCheckComplete)
      .pipe(
        tap((isLoginComplete) => {
          if (isLoginComplete) {
            this.store$
              .select(getIsLoggedIn)
              .pipe(
                tap((isLoggedIn) => {
                  // but unable to login (this means there is no previous token to use to re-direct to admin)
                  if (!isLoggedIn) {
                    this.setUiStore(true);
                  } else {
                    this.setUiStore(false);
                    this.store$
                      .select(getStrataPlanNumberFriendlyName)
                      .pipe(
                        tap((friendlyName) => {
                          // logged-in user hit F5 to clear store, refetch all based on LoginId of principle
                          if (friendlyName === '') {
                            this.store$.dispatch(
                              new FetchLotsAndUnitsWithStrataPlan()
                            );
                          }
                        }),
                        takeUntil(this.componentDestroyed$)
                      )
                      .subscribe();
                  }
                }),
                takeUntil(this.componentDestroyed$)
              )
              .subscribe();
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.store$.dispatch(new ApproverCheckLogin());
    this.store$.dispatch(new SearcherCheckLogin());

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          )
        )
        .subscribe((event) => {
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          const pageTitle = route.snapshot.data['title'];
          this.titleService.setTitle(pageTitle ? `${pageTitle} - StrataSphere` : 'StrataSphere');

          const cleanUrl = event.url.replace(/\/\d+/g, '/id');
          const gtmTag = {
            event: 'page',
            pageName: cleanUrl,
          };

          this.googleTagManagerService.pushTag(gtmTag).catch((error) => {
            console.error('Failed to push GTM tag:', error);
          });
        })
    );
  }

  private setUiStore(setStrataPlanNumber: boolean) {
    let subDomain = this.getSubDomain();
    this.store$.dispatch(new SetSubDomain(subDomain));
    this.store$.dispatch(new ApproverSetSubDomain(subDomain));
    if (subDomain !== null && setStrataPlanNumber) {
      let strataPlanNumberFriendlyName = this.getStrataPlanNumberFriendlyName();
      if (strataPlanNumberFriendlyName !== null) {
        this.store$.dispatch(
          new SetStrataPlanFriendlyName(strataPlanNumberFriendlyName)
        );
        if (
          localStorage.getItem(environment.auth.user_login) !==
          UserLogin.Approver
        ) {
          this.store$.dispatch(
            new FetchLotsAndUnits(strataPlanNumberFriendlyName)
          );
        }
      }
    }
  }

  // strataplus.stratasphere.tech or stratasphere.tech
  private getSubDomain(): string {
    let hostname = window.location.hostname;

    if (hostname.indexOf('.') < 0) {
      return localStorage.getItem(environment.ui.sub_domain);
    }
    const fullDomain = hostname.split('.');

    if (fullDomain.length < 3) {
      return null;
    }
    const newSubDomain = fullDomain[0];
    // check localstorage to make sure someone clever has not modified the subdomain in url
    const currentSubDomain = localStorage.getItem(environment.ui.sub_domain);
    if (currentSubDomain !== null && newSubDomain !== currentSubDomain) {
      // clear everything
      localStorage.removeItem(environment.ui.sub_domain);
      localStorage.removeItem(environment.ui.body_corporate_id);
      localStorage.removeItem(environment.ui.strata_plan_friendly_name);
      localStorage.removeItem(environment.auth.token);
      localStorage.removeItem(environment.auth.token_expiry);
      localStorage.removeItem(environment.auth.refresh_token);
      this.getSubDomain(); // restart process
    }
    // update localStorage with new subdomain
    localStorage.setItem(environment.ui.sub_domain, newSubDomain);
    return newSubDomain;
  }

  private getStrataPlanNumberFriendlyName() {
    let path = this.locationStrategy.path().split('/');
    let roles: string[] = [
      UserLogin.Admin,
      UserLogin.Approver,
      UserLogin.Searcher,
      UserLogin.Manager,
      UserLogin.ViewPdf,
    ];
    let pathCheck = roles.includes(path[1]);
    if (
      path.length > 1 &&
      !pathCheck &&
      path[1] !== '' &&
      path[1] !== 'sessions'
    ) {
      const friendlyName = path[1];
      localStorage.setItem(
        environment.ui.strata_plan_friendly_name,
        friendlyName
      );
      return friendlyName;
    }
    let friendly = null;
    if (path[1] !== UserLogin.Manager) {
      friendly = localStorage.getItem(environment.ui.strata_plan_friendly_name);
    }

    if (friendly !== null) {
      this.store$.dispatch(new SetStrataPlanFriendlyName(friendly));
      let urlToNavigate = environment.auth.home_url.replace('id', friendly);
      this.store$.dispatch(new SetStrataPlanFriendlyName(friendly));
      this.store$.dispatch(new FetchLotsAndUnits(friendly));
      // do not use router.navigate. When session times out and someone hits f5, routes will be set incorrectly
      this.router.navigateByUrl(urlToNavigate);
    } else if (path[1] === UserLogin.Manager) {
    } else {
      this.router.navigateByUrl(environment.auth.not_found);
    }

    if (path[1] === UserLogin.ViewPdf) {
      this.router.navigate([environment.auth.login_url], {
        queryParams: { redirectTo: location.pathname },
      });
    }

    return null;
  }

  private checkPathUserLogin() {
    let path = this.locationStrategy.path().split('/');
    // (path[1] will be null when page refresh
    if (path[1] === '') {
      return;
    }
    if (
      path.length > 1 &&
      (path[1] === 'approver' || path[1] === 'admin' || path[1] === 'pdf')
    ) {
      return;
    } else {
      localStorage.setItem(environment.auth.user_login, 'user_login');
    }
    return;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
    this.subscriptions.unsubscribe();
  }
}
